import {
  AddOutlined,
  ButtonDS,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  LaunchPage,
  NotInterestedOutlined,
  RemoveRedEyeOutlined,
  SidePanelDS,
  Spacer,
  Tag,
  TextCapitalized,
  TitleInputDS,
  isNameDuplicate,
  triggerToastDS,
} from "@qivia/ui";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import styled from "styled-components";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { listAllVehiclesInDbAsync, selectListAllVehiclesInDb, selectListAllVehiclesInDbStatus } from "../../vehicles/vehiclesSlice";
import {
  departmentsSlice,
  selectDepartments,
  selectUpdateDepartmentNameStatus,
  updateDepartmentNameAsync,
} from "../departmentsSlice";
import { selectCompany } from "../../homeSlice";
import { DepartmentDeleteModal } from "../modals/departmentDeleteModal";
import { AddVehicleToDepartmentModal } from "../modals/addVehicleToDepartmentModal";
import { RemoveVehicleFromDepartmentModal } from "../modals/removeVehicleFromDepartmentModal";

export interface DepartmentSidePanelProps {
  canAddVehicle?: boolean;
  canDeactivate?: boolean;
  canUpdateName?: boolean;
}

export const DepartmentSidePanel = (props: DepartmentSidePanelProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const firstPath = location.pathname.split("/")[1]; 
  const params = useParams();

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isAddVehicleModalVisible, setIsAddVehicleModalVisible] = useState(false);
  const [isEditModeActive, setIsEditModeActive] = useState(false);
  const [vehicleUuidToRemove, setVehicleUuidToRemove] = useState<string | null>(null);
  const [departmentName, setDepartmentName] = useState<string>("");
  const [displayError, setDisplayError] = useState<boolean>(false);
  const [isNameEditModeActive, setIsNameEditModeActive] = useState<boolean>(false);

  const company = useAppSelector(selectCompany);
  const departments = useAppSelector(selectDepartments);
  const vehicles = useAppSelector(selectListAllVehiclesInDb);
  const listAllVehiclesInDbStatus = useAppSelector(selectListAllVehiclesInDbStatus);
  const updateDepartmentNameStatus = useAppSelector(selectUpdateDepartmentNameStatus);

  const department = useMemo(() => {
    if (departments) {
        return departments.find((department) => department.uuid === params.id);
    }
  }, [departments, params.id]);

  const otherDepartments = useMemo(() => {
    return departments.filter((departmentItem) => departmentItem.uuid !== params.id);
  }, [departments, params.id]);

  const isDepartmentNameValid: boolean = useMemo(
    () =>
      !!department &&
      departmentName !== "" &&
      departmentName.length <= 28 &&
      departmentName !== department.name &&
      !isNameDuplicate(otherDepartments, departmentName.trim()),
    [department, departmentName, otherDepartments],
  );

  const departmentNameError: string | undefined = useMemo(() => {
    if (!displayError) return undefined;
    if (departmentName === "") return t("departments.modal.error.empty");
    if (isNameDuplicate(otherDepartments, departmentName.trim())) {
      return t("departments.modal.error.nameAlreadyExists");
    }
    return departmentName.length > 28
      ? t("departments.modal.error.length")
      : undefined;
  }, [departmentName, displayError, otherDepartments]);

  const vehiclesByDepartment = useMemo(() => {
    return vehicles.filter((vehicle) => vehicle.department === params.id);
  }, [params.id, vehicles]);

  useEffect(() => {
    if (!department) {
      navigate(`/${firstPath}/departments`);
    }
  }, [navigate, department, firstPath, vehiclesByDepartment]);

  useEffect(() => {
    if (department) {
      setDepartmentName(department.name);
      setDisplayError(false);
    }
  }, [department]);

  useEffect(() => {
    if (updateDepartmentNameStatus === "success") {
      triggerToastDS(t("departments.update.success") + departmentName, "SUCCESS");
      void dispatch(departmentsSlice.actions.resetUpdateDepartmentNameStatus());
      void dispatch(departmentsSlice.actions.resetDepartmentsListStatus());
      setIsNameEditModeActive(false);
    } else if (updateDepartmentNameStatus === "failed") {
      triggerToastDS(t("departments.update.failure"), "ERROR");
      void dispatch(departmentsSlice.actions.resetUpdateDepartmentNameStatus());
      setIsNameEditModeActive(false);
    }
  }, [departmentName, dispatch, updateDepartmentNameStatus]);

  useEffect(() => {
    if (company && listAllVehiclesInDbStatus === "idle") {
      void dispatch(listAllVehiclesInDbAsync({ companyUuid: company.uuid }));
    }
  }, [company, dispatch, listAllVehiclesInDbStatus]);

  const onRemoveVehicleFromDepartment = useCallback((vehicleUuid: string) => {
    setVehicleUuidToRemove(vehicleUuid);
  }, []);

  const onUpdateDepartmentName = useCallback(() => {
    setDisplayError(true);
    if (!company || !department || !isDepartmentNameValid) return;

    void dispatch(
      updateDepartmentNameAsync({
        companyUuid: company.uuid,
        departmentUuid: department.uuid,
        name: departmentName,
      }),
    );
  }, [company, department, departmentName, dispatch, isDepartmentNameValid]);

  const onCancelDepartmentName = useCallback(() => {
    if (!department) return;
    setDepartmentName(department.name);
    setDisplayError(false);
    setIsNameEditModeActive(false);
  }, [department]);

  const onSidePanelClosed = useCallback(() => {
    setIsEditModeActive(false);
    setIsNameEditModeActive(false);
    navigate(`/${firstPath}/departments`);
  }, [firstPath, navigate]);

  if (!department) {
    return;
  }

  if (!vehiclesByDepartment) return <LaunchPage hasBorderRadius={true} />;

  return (
    <SidePanelDS.SidePanel
      visible={true}
      onClose={onSidePanelClosed}
      absoluteSection={props.canAddVehicle || props.canDeactivate ? (
        <>
          {props.canAddVehicle && (
            <>
              <ButtonDS
                leftIcon={<AddOutlined />}
                text={t("departments.sidepanel.addVehicle")}
                format={"fill"}
                buttonType={"primary"}
                onClick={() => setIsAddVehicleModalVisible(true)}
                color={"primary"}
              />
              <Spacer y={1} />
            </>
          )}
          {props.canDeactivate && (
            <ButtonDS
              leftIcon={<NotInterestedOutlined />}
              text={t("departments.sidepanel.delete")}
              format={"fill"}
              buttonType={"secondary"}
              onClick={() => setIsDeleteModalVisible(true)}
              iconColor={"errorNormal"}
            />
          )}
        </>
        ) : undefined
      }
    >
      <SidePanelDS.Section verticalSpacing={1.2} horizontalSpacing={0.5}>
        <StyledPanelContainer>
          {isNameEditModeActive ? (
            <StyledDepartmentNameRow>
              <StyledDepartmentName>
                <TitleInputDS
                  size="S"
                  value={departmentName}
                  onChange={(e: string) => setDepartmentName(e.toLocaleUpperCase())}
                  placeholder={t("departments.modal.department.placeholder") || ""}
                />
              </StyledDepartmentName>
              <StyledDepartmentNameActions>
                <ButtonDS
                  buttonType="primary"
                  format="hug"
                  singleIcon={{ icon: <CheckOutlined />, size: "S" }}
                  height="2"
                  onClick={onUpdateDepartmentName}
                  disabled={!isDepartmentNameValid}
                />
                <Spacer x={1} />
                <ButtonDS
                  buttonType="secondary"
                  format="hug"
                  singleIcon={{ icon: <CloseOutlined />, size: "S" }}
                  height="2"
                  onClick={onCancelDepartmentName}
                />
              </StyledDepartmentNameActions>
            </StyledDepartmentNameRow>
          ) : (
            <StyledDepartmentNameRow>
              <StyledContent>{department.name}</StyledContent>
              {props.canUpdateName && (
                <StyledDepartmentNameActions>
                  <ButtonDS
                    buttonType="tertiary"
                    format="hug"
                    singleIcon={{ icon: <EditOutlined />, size: "S" }}
                    height="2"
                    onClick={() => setIsNameEditModeActive(true)}
                  />
                </StyledDepartmentNameActions>
              )}
            </StyledDepartmentNameRow>
          )}
          {departmentNameError && (
            <>
              <Spacer y={0.5} />
              <StyledDepartmentNameError>
                <TextCapitalized>{departmentNameError}</TextCapitalized>
              </StyledDepartmentNameError>
            </>
          )}
        </StyledPanelContainer>
      </SidePanelDS.Section>

      <SidePanelDS.Section verticalSpacing={1.5} horizontalSpacing={1.5}>
        <StyledSectionTitle>
          <TextCapitalized>
            {t("departments.sidepanel.associatedVehicles")}
          </TextCapitalized>
          {props.canAddVehicle && (
            <ButtonDS 
              singleIcon={{ icon: <EditOutlined />, size: "S" }} 
              format="fill"
              height="2.5"
              buttonType={isEditModeActive ? "primary" : "tertiary"}
              onClick={() => setIsEditModeActive(!isEditModeActive)}
            />
          )}
        </StyledSectionTitle>
        <Spacer y={2} />
        {vehiclesByDepartment.length > 0 ? (
          <StyledVehicleList>
            {vehiclesByDepartment.map((vehicle) => (
                <StyledVehicleItem key={vehicle.uuid}>
                    <SidePanelDS.SectionedActionStripe
                    left={
                      <StyledRowCenter>
                        {vehicle.registrationNumber && (
                          <>
                            <StyledVehicleResume>{vehicle.registrationNumber}</StyledVehicleResume>
                            <Spacer x={1} />
                          </>
                        )}
                        {vehicle.reference && (
                            <>
                              <Tag
                                textColor={colors["colors/text/darkGrey"]}
                                text={vehicle.reference}
                                backgroundColor={
                                colors["colors/surfaces/background/background_level0"]
                                }
                                borderColor={colors["colors/text/darkGrey"]}
                              />
                              <Spacer x={0.5} />
                            </>
                        )}
                        </StyledRowCenter>
                    }
                    right={
                      <>
                        {isEditModeActive ? (
                            <StyledRemoveIcon
                              onClick={() => onRemoveVehicleFromDepartment(vehicle.uuid)}
                            >
                              <CloseOutlined />
                              <Spacer x={1} />
                            </StyledRemoveIcon>
                        ) : (
                          <StyledLittleIcon>
                            <RemoveRedEyeOutlined
                              key="vehicle"
                              onClick={() =>
                                navigate(`/home/vehicles/${vehicle.uuid}`)
                              }
                            />
                            <Spacer x={1} />
                          </StyledLittleIcon>
                        )}
                      </>
                    }
                    />
                </StyledVehicleItem>
            ))}
        </StyledVehicleList>
        ) : (
          <StyledRowCenter>
            <StyledEmptyVehiclesList>
              <TextCapitalized>
                {t("departments.sidepanel.empty")}
              </TextCapitalized>
            </StyledEmptyVehiclesList>
          </StyledRowCenter>
        )}
      </SidePanelDS.Section>
      <AddVehicleToDepartmentModal
        visible={isAddVehicleModalVisible}
        departmentUuid={department.uuid}
        onCloseModal={() => {
          setIsAddVehicleModalVisible(false);
        }}
      />
      <DepartmentDeleteModal 
        isVisible={isDeleteModalVisible} 
        departmentUuid={department.uuid} 
        onCloseModal={() => setIsDeleteModalVisible(false)}
      />
      <RemoveVehicleFromDepartmentModal
          isVisible={!!vehicleUuidToRemove}
          departmentUuid={department.uuid}
          departmentName={department.name}
          vehicleUuid={vehicleUuidToRemove}
          onCloseModal={() => setVehicleUuidToRemove(null)}
      />
    </SidePanelDS.SidePanel>
  );
};

const StyledSectionTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  & button svg {
    width: 1.25rem;
    height: 1.25rem;
  };
`;

const StyledPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const StyledDepartmentNameRow = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const StyledDepartmentName = styled.div`
  display: flex;
  align-items: center;
  width: 80%;
  z-index: 2;

  & input {
    text-align: center;
  }
`;

const StyledDepartmentNameActions = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  z-index: 3;

  & button svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`;

const StyledDepartmentNameError = styled.div`
  ${typographies["Body/S"]};
  color: ${colors["colors/system/error/error_normal"]};
`;

const StyledContent = styled.div`
  ${typographies["Header/H4"]};
`;

const StyledRowCenter = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const StyledRemoveIcon = styled.div`
  display: flex;
  justify-content: center;
  cursor: pointer;
  fill: ${colors["colors/system/error/error_normal"]};
  & svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    fill: ${colors["colors/text/black"]};
  }
`;

const StyledLittleIcon = styled.div`
  display: flex;
  cursor: pointer;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledVehicleResume = styled.span`
  ${typographies["Body/XS"]};
  color: ${colors["colors/text/black"]};
  text-align: center;
`;

const StyledVehicleList = styled.div`
  border: .025rem solid ${colors["colors/text/veryLightGrey"]};
  border-radius: 1rem;
  overflow: hidden;
`;

const StyledVehicleItem = styled.div`
  &:not(:first-child) {
    border-top: .025rem solid ${colors["colors/text/veryLightGrey"]};
  }
`;

const StyledEmptyVehiclesList = styled.div`
  ${typographies["Body/XS"]}
  color: ${colors["colors/text/darkGrey"]};
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
